// Authentication Modal Web Component


class AuthModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.currentMode = 'login'; // 'login', 'register', 'forgot-password', 'reset-password', '2fa-setup'
    this.isRequired = false; // Whether auth is required (can't be closed)
    this.resetToken = null; // For password reset flow
    this.invitationToken = null; // For site friend invitation flow
    this.prefillEmail = null; // For group invite registration prefill
    this.twoFactorData = null; // For 2FA setup
    this.pendingLoginData = null; // For 2FA verification
  }

  connectedCallback() {
    window.logger.component('connected', 'auth-modal');
    this.render();
    this.attachEventListeners();
  }

  render() {
    this.innerHTML = `
      <div class="auth-modal-overlay ${this.isOpen ? 'open' : ''}" id="authModalOverlay">
        <div class="auth-modal">
          <div class="auth-modal-header">
            <h2 class="auth-modal-title" id="authModalTitle">
              ${this.getModalTitle()}
            </h2>
            ${!this.isRequired ? '<button class="auth-modal-close" id="authModalClose" aria-label="Close">✕</button>' : ''}
          </div>

          <div class="auth-modal-body">
            ${this.currentMode !== 'verify-email' ? `
            <div class="auth-tabs">
              <button class="auth-tab ${this.currentMode === 'login' ? 'active' : ''}" 
                      data-mode="login">
                Login
              </button>
              <button class="auth-tab ${this.currentMode === 'register' ? 'active' : ''}" 
                      data-mode="register">
                Sign Up
              </button>
              ${this.currentMode === 'forgot-password' || this.currentMode === 'reset-password' ?
    '<button class="auth-tab active" data-mode="forgot-password">Reset Password</button>' : ''}
            </div>
            ` : ''}

            <form class="auth-form" id="authForm">
              ${this.renderFormFields()}
              <div class="form-actions">
                <button type="submit" class="btn btn-primary btn-lg auth-submit-btn">
                  <span class="btn-text">
                    ${this.getSubmitButtonText()}
                  </span>
                  <span class="spinner hidden"></span>
                </button>
              </div>
            </form>

            <div class="auth-footer">
              ${this.renderAuthFooter()}
            </div>

            <div class="alert-container" id="authAlertContainer"></div>
          </div>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Close modal (only if not required)
    const closeBtn = this.querySelector('#authModalClose');
    const overlay = this.querySelector('#authModalOverlay');

    closeBtn?.addEventListener('click', () => {
      if (!this.isRequired) this.close();
    });

    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay && !this.isRequired) this.close();
    });

    // Tab switching
    const tabs = this.querySelectorAll('.auth-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const mode = tab.dataset.mode;
        this.switchMode(mode);
      });
    });

    // Auth link switching
    const authLinks = this.querySelectorAll('.auth-link');
    authLinks.forEach(link => {
      link.addEventListener('click', () => {
        const mode = link.dataset.mode;
        this.switchMode(mode);
      });
    });

    // Form submission
    const form = this.querySelector('#authForm');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Keyboard handling
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen && !this.isRequired) {
        this.close();
      }
    });
  }

  switchMode(mode) {
    this.currentMode = mode;
    this.render();
    this.attachEventListeners();
  }

  async handleSubmit() {
    const form = this.querySelector('#authForm');
    const formData = new FormData(form);
    const data = Object.fromEntries(formData);

    this.setLoading(true);
    this.clearAlerts();
    this.clearFieldErrors();

    try {
      let endpoint, requestData;

      switch (this.currentMode) {
      case 'login':
        endpoint = '/auth/login';
        requestData = data;
        break;
      case 'register':
        // Validate password confirmation on client side
        if (data.password !== data.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        endpoint = '/auth/register';
        requestData = data;
        break;
      case 'forgot-password':
        endpoint = '/auth/forgot-password';
        requestData = { email: data.email };
        break;
      case 'reset-password':
        if (data.password !== data.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        endpoint = '/auth/reset-password';
        requestData = { token: this.resetToken, password: data.password };
        break;
      case '2fa-setup':
        endpoint = '/auth/2fa/verify';
        requestData = { token: data.verificationToken };
        break;
      case '2fa-verify':
        endpoint = '/auth/login';
        requestData = { ...this.pendingLoginData, twoFactorToken: data.twoFactorToken };
        break;
      case 'verify-email':
        endpoint = '/auth/resend-verification';
        requestData = { email: this.verificationEmail };
        break;
      default:
        throw new Error('Invalid form mode');
      }

      // Use centralized API helper
      if (!window.API) {
        throw new Error('API not available');
      }

      const result = await window.API.request(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      // Handle success based on mode
      switch (this.currentMode) {
      case 'login':
        // Check if 2FA is required
        if (result.requiresTwoFactor) {
          this.pendingLoginData = data; // Store login data for 2FA verification
          this.showAlert('Please enter your 2FA code', 'info');
          this.switchMode('2fa-verify');
          return;
        }

        this.showAlert('Success! Redirecting...', 'success');

        // Dispatch custom event
        this.dispatchEvent(new window.CustomEvent('auth-success', {
          detail: { mode: this.currentMode, user: result.user },
          bubbles: true
        }));

        // Close modal immediately before navigation
        this.isRequired = false;
        this.close();

        this.schedulePostAuthNavigation();
        break;

      case 'register':
        if (result.requiresEmailVerification) {
          this.showAlert('Registration successful! Please check your email to verify your account.', 'success');
          // Switch to email verification mode
          this.currentMode = 'verify-email';
          this.verificationEmail = result.email;
          this.render();
          this.attachEventListeners();
        } else if (result.user) {
          // Email already verified (invitation-based registration)
          this.showAlert(result.message, 'success');

          // Dispatch custom event
          this.dispatchEvent(new CustomEvent('auth-success', {
            detail: { mode: this.currentMode, user: result.user },
            bubbles: true
          }));

          // Close modal immediately before navigation
          this.isRequired = false;
          this.close();

          this.schedulePostAuthNavigation();
        } else {
          // Fallback for old response format
          this.showAlert(result.invitationAccepted
            ? 'Account created and invitation accepted! You\'re now friends! 🎉'
            : 'Account created! Redirecting...', 'success');

          // Dispatch custom event
          this.dispatchEvent(new CustomEvent('auth-success', {
            detail: { mode: this.currentMode, user: result.user, invitationAccepted: result.invitationAccepted },
            bubbles: true
          }));

          // Close modal immediately before navigation
          this.isRequired = false;
          this.close();

          this.schedulePostAuthNavigation();
        }
        break;

      case 'verify-email':
        this.showAlert('Verification email sent! Please check your inbox.', 'success');
        break;

      case 'forgot-password':
        this.showAlert('Password reset email sent! Check your inbox.', 'success');
        setTimeout(() => {
          this.currentMode = 'login';
          this.render();
          this.attachEventListeners();
        }, 2000);
        break;

      case 'reset-password':
        this.showAlert('Password reset successful! You can now loginin.', 'success');
        setTimeout(() => {
          this.currentMode = 'login';
          this.render();
          this.attachEventListeners();
        }, 2000);
        break;

      case '2fa-setup':
        this.showAlert('Two-factor authentication enabled successfully!', 'success');

        this.dispatchEvent(new CustomEvent('auth-success', {
          detail: { mode: this.currentMode, twoFactorEnabled: true },
          bubbles: true
        }));

        setTimeout(() => {
          this.close();
        }, 2000);
        if (window.router) {
          window.router.navigate('/settings/security', true);
        }
        break;

      case '2fa-verify':
        this.showAlert('2FA verified! Redirecting...', 'success');

        // Dispatch custom event
        this.dispatchEvent(new window.CustomEvent('auth-success', {
          detail: { mode: this.currentMode, user: result.user },
          bubbles: true
        }));

        // Close modal immediately before navigation
        this.isRequired = false;
        this.close();

        this.schedulePostAuthNavigation();
        break;
      }

    } catch (error) {
      window.logger.error('Form submission error', error, { component: 'auth-modal', mode: this.currentMode });

      // Show field-specific errors if they exist
      if (error.details && Array.isArray(error.details) && error.details.length > 0) {
        this.showFieldErrors(error.details);
        // Show a more helpful general message
        const message = error.error || 'Please correct the errors above';
        this.showAlert(message, 'error');
      } else {
        // Show generic error message
        this.showAlert(error.message, 'error');
      }
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    const submitBtn = this.querySelector('.auth-submit-btn');
    const btnText = this.querySelector('.btn-text');
    const spinner = this.querySelector('.spinner');

    if (isLoading) {
      submitBtn.disabled = true;
      btnText.textContent = this.getLoadingButtonText();
      spinner.classList.remove('hidden');
    } else {
      submitBtn.disabled = false;
      btnText.textContent = this.getSubmitButtonText();
      spinner.classList.add('hidden');
    }
  }

  getLoadingButtonText() {
    switch (this.currentMode) {
    case 'login': return 'Signing In...';
    case 'register': return 'Creating Account...';
    case 'verify-email': return 'Checking Email...';
    case 'forgot-password': return 'Sending Email...';
    case 'reset-password': return 'Resetting Password...';
    case '2fa-setup': return 'Enabling 2FA...';
    case '2fa-verify': return 'Verifying...';
    default: return 'Loading...';
    }
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('#authAlertContainer');
    const alert = document.createElement('div');
    alert.className = `alert alert-${type}`;
    alert.textContent = message;

    if (container) {
      container.appendChild(alert);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentNode) {
          alert.remove();
        }
      }, 5000);
    }
  }

  clearAlerts() {
    const container = this.querySelector('#authAlertContainer');
    if (container) {
      container.innerHTML = '';
    }
  }

  /**
   * Clear all field-level error messages
   */
  clearFieldErrors() {
    const errorElements = this.querySelectorAll('.field-error');
    errorElements.forEach(el => el.remove());

    const invalidFields = this.querySelectorAll('.form-control.is-invalid');
    invalidFields.forEach(el => el.classList.remove('is-invalid'));
  }

  /**
   * Display field-specific validation errors
   * @param {Array} details - Array of validation error details from server
   */
  showFieldErrors(details) {
    if (!details || !Array.isArray(details)) return;

    details.forEach(detail => {
      let field = detail.field;
      field = field.substring(field.lastIndexOf('/') + 1);
      const message = detail.message;

      // Find the input field
      const input = this.querySelector(`[name="${field}"]`);
      if (!input) return;

      // Add error class to input
      input.classList.add('is-invalid');

      // Create error message element
      const errorDiv = document.createElement('div');
      errorDiv.className = 'field-error';
      errorDiv.textContent = message;

      // Insert error message after the input (or after form-text if it exists)
      const formGroup = input.closest('.form-group');
      if (formGroup) {
        const formText = formGroup.querySelector('.form-text');
        if (formText) {
          formText.after(errorDiv);
        } else {
          input.after(errorDiv);
        }
      } else {
        input.after(errorDiv);
      }
    });
  }

  schedulePostAuthNavigation() {
    setTimeout(() => {
      if (window.router?.navigateAfterAuth) {
        window.router.navigateAfterAuth();
      } else if (window.router) {
        window.router.navigate('/', true);
      }
    }, 100);
  }

  open(mode = 'login', required = false) {
    this.currentMode = mode;
    this.isOpen = true;
    this.isRequired = required;
    this.render();
    this.attachEventListeners();

    // Focus first input
    setTimeout(() => {
      const firstInput = this.querySelector('input:not([type="hidden"])');
      firstInput?.focus();
    }, 100);
  }

  close() {
    this.isOpen = false;
    const overlay = this.querySelector('#authModalOverlay');
    if (overlay) {
      overlay.classList.remove('open');
    }

    // Dispatch close event
    this.dispatchEvent(new window.CustomEvent('auth-modal-closed', {
      bubbles: true
    }));
  }

  getModalTitle() {
    switch (this.currentMode) {
    case 'login': return 'Welcome Back!';
    case 'register': return 'Join Fun Old Times!';
    case 'verify-email': return 'Check Your Email';
    case 'forgot-password': return 'Reset Your Password';
    case 'reset-password': return 'Enter New Password';
    case '2fa-setup': return 'Setup Two-Factor Authentication';
    case '2fa-verify': return 'Two-Factor Authentication';
    default: return 'Authentication';
    }
  }

  getSubmitButtonText() {
    switch (this.currentMode) {
    case 'login': return 'Login';
    case 'register': return 'Create Account';
    case 'verify-email': return 'Resend Email';
    case 'forgot-password': return 'Send Reset Email';
    case 'reset-password': return 'Reset Password';
    case '2fa-setup': return 'Enable 2FA';
    case '2fa-verify': return 'Verify';
    default: return 'Submit';
    }
  }

  renderFormFields() {
    switch (this.currentMode) {
    case 'login':
      return this.renderLoginFields();
    case 'register':
      return this.renderRegisterFields();
    case 'verify-email':
      return this.renderVerifyEmailFields();
    case 'forgot-password':
      return this.renderForgotPasswordFields();
    case 'reset-password':
      return this.renderResetPasswordFields();
    case '2fa-setup':
      return this.render2FASetupFields();
    case '2fa-verify':
      return this.render2FAVerifyFields();
    default:
      return this.renderLoginFields();
    }
  }

  renderLoginFields() {
    return `
      <div class="form-group">
        <label class="form-label" for="email">Email</label>
        <input 
          type="email" 
          class="form-control" 
          id="email" 
          name="email"
          placeholder="your@email.com"
          required
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="password">Password</label>
        <input 
          type="password" 
          class="form-control" 
          id="password" 
          name="password"
          placeholder="Enter your password"
          required
        />
      </div>
    `;
  }

  renderRegisterFields() {
    return `
      <div class="form-group">
        <label class="form-label" for="firstName">First Name</label>
        <input 
          type="text" 
          class="form-control" 
          id="firstName" 
          name="first_name"
          placeholder="Your first name"
          required
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="lastName">Last Name</label>
        <input 
          type="text" 
          class="form-control" 
          id="lastName" 
          name="last_name"
          placeholder="Your last name"
          required
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="username">Username</label>
        <input 
          type="text" 
          class="form-control" 
          id="username" 
          name="username"
          placeholder="Choose a unique username"
          required
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="email">Email</label>
        <input 
          type="email" 
          class="form-control" 
          id="email" 
          name="email"
          placeholder="your@email.com"
          value="${(this.prefillEmail || '').replace(/"/g, '&quot;')}"
          required
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="password">Password</label>
        <input 
          type="password" 
          class="form-control" 
          id="password" 
          name="password"
          placeholder="At least 8 characters"
          minlength="8"
          required
        />
        <div class="form-text">Must be at least 8 characters long</div>
      </div>
      <div class="form-group">
        <label class="form-label" for="confirmPassword">Confirm Password</label>
        <input 
          type="password" 
          class="form-control" 
          id="confirmPassword" 
          name="confirmPassword"
          placeholder="Confirm your new password"
          minlength="8"
          required
        />
      </div>
      
      ${this.invitationToken ? `
        <input type="hidden" name="invitationToken" value="${this.invitationToken}" />
        <div class="invitation-notice">
          <div class="alert alert-info">
            <strong>🎉 You're invited!</strong><br>
            You'll automatically become friends with the person who invited you when you sign up.
          </div>
        </div>
      ` : ''}
    `;
  }

  renderVerifyEmailFields() {
    return `
      <div class="verification-info">
        <div class="verification-icon">📧</div>
        <p>We've sent a verification email to <strong>${this.verificationEmail || 'your email address'}</strong>.</p>
        <p>Please check your inbox and click the verification link to activate your account.</p>
        <p>Didn't receive the email? Check your spam folder or click "Resend Email" below.</p>
      </div>
    `;
  }

  renderForgotPasswordFields() {
    return `
      <div class="form-group">
        <label class="form-label" for="email">Email Address</label>
        <input 
          type="email" 
          class="form-control" 
          id="email" 
          name="email"
          placeholder="Enter your email address"
          required
        />
        <div class="form-text">We'll send you a link to reset your password</div>
      </div>
    `;
  }

  renderResetPasswordFields() {
    return `
      <div class="form-group">
        <label class="form-label" for="password">New Password</label>
        <input 
          type="password" 
          class="form-control" 
          id="password" 
          name="password"
          placeholder="Enter your new password"
          minlength="8"
          required
        />
        <div class="form-text">Must be at least 8 characters long</div>
      </div>

      <div class="form-group">
        <label class="form-label" for="confirmPassword">Confirm Password</label>
        <input 
          type="password" 
          class="form-control" 
          id="confirmPassword" 
          name="confirmPassword"
          placeholder="Confirm your new password"
          minlength="8"
          required
        />
      </div>
    `;
  }

  render2FASetupFields() {
    if (!this.twoFactorData) {
      return '<div class="form-text">Setting up 2FA...</div>';
    }

    return `
      <div class="form-group">
        <label class="form-label">Scan QR Code</label>
        <div class="qr-code-container">
          <img src="${this.twoFactorData.qrCode}" alt="2FA QR Code" class="qr-code" />
        </div>
        <div class="form-text">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</div>
      </div>

      <div class="form-group">
        <label class="form-label" for="manualKey">Manual Entry Key</label>
        <div class="manual-key-container">
          <input 
            type="text" 
            class="form-control" 
            id="manualKey" 
            value="${this.twoFactorData.manualEntryKey}"
            readonly
          />
          <button type="button" class="btn btn-sm btn-outline copy-btn" onclick="navigator.clipboard.writeText('${this.twoFactorData.manualEntryKey}')">
            Copy
          </button>
        </div>
        <div class="form-text">Or manually enter this key in your authenticator app</div>
      </div>

      <div class="form-group">
        <label class="form-label" for="verificationToken">Verification Code</label>
        <input 
          type="text" 
          class="form-control" 
          id="verificationToken" 
          name="verificationToken"
          placeholder="Enter 6-digit code from your app"
          maxlength="6"
          pattern="[0-9]{6}"
          autocomplete="one-time-code"
          inputmode="numeric"
          required
        />
        <div class="form-text">Enter the 6-digit code from your authenticator app</div>
      </div>
    `;
  }

  render2FAVerifyFields() {
    return `
      <div class="form-group">
        <label class="form-label" for="twoFactorToken">Verification Code</label>
        <input 
          type="text" 
          class="form-control" 
          id="twoFactorToken" 
          name="twoFactorToken"
          placeholder="Enter 6-digit code from your authenticator app"
          maxlength="6"
          pattern="[0-9]{6}"
          autocomplete="one-time-code"
          inputmode="numeric"
          required
        />
        <div class="form-text">Enter the current code from your authenticator app</div>
      </div>
    `;
  }

  renderAuthFooter() {
    switch (this.currentMode) {
    case 'login':
      return `
          <p class="auth-switch">
            Don't have an account? <button type="button" class="auth-link" data-mode="register">Sign up here</button>
          </p>
          <p class="auth-switch">
            Forgot your password? <button type="button" class="auth-link" data-mode="forgot-password">Reset it here</button>
          </p>
        `;
    case 'register':
      return `
          <p class="auth-switch">
            Already have an account? <button type="button" class="auth-link" data-mode="login">Login here</button>
          </p>
        `;
    case 'forgot-password':
      return `
          <p class="auth-switch">
            Remember your password? <button type="button" class="auth-link" data-mode="login">Login here</button>
          </p>
        `;
    case 'reset-password':
      return `
          <p class="auth-switch">
            Remember your password? <button type="button" class="auth-link" data-mode="login">Login here</button>
          </p>
        `;
    case '2fa-setup':
      return `
          <p class="auth-switch">
            <button type="button" class="auth-link" data-mode="login">Back to Login</button>
          </p>
        `;
    case '2fa-verify':
      return `
          <p class="auth-switch">
            <button type="button" class="auth-link" data-mode="login">Back to Login</button>
          </p>
        `;
    default:
      return '';
    }
  }

  // Method to open forgot password modal
  openForgotPassword() {
    this.open('forgot-password', false);
  }

  // Method to open reset password modal with token
  openResetPassword(token) {
    this.resetToken = token;
    this.open('reset-password', false);
  }

  // Method to open register modal with invitation token
  openRegister(invitationToken = null) {
    this.openRegisterWithOptions({ invitationToken });
  }

  openRegisterWithOptions({ invitationToken = null, prefillEmail = null } = {}) {
    this.invitationToken = invitationToken;
    this.prefillEmail = prefillEmail;
    this.open('register', false);
  }

  // Method to open 2FA setup modal
  async open2FASetup() {
    try {
      // Use centralized API helper
      if (!window.API) {
        throw new Error('API not available');
      }

      this.twoFactorData = await window.API.request('/auth/2fa/setup', {
        method: 'POST'
      });

      this.open('2fa-setup', false);
    } catch (error) {
      window.logger.error('2FA setup error', error, { component: 'auth-modal' });
      this.showAlert('Failed to setup 2FA. Please try again.', 'error');
    }
  }
}

// Register the custom element
customElements.define('auth-modal', AuthModal);
